import { Component, Input, OnInit } from '@angular/core';
import { BlockDataService } from 'src/app/block-services/block-data.service';
import { BlockFieldsService } from 'src/app/block-services/block-fields.service';
import { BuilderService } from 'src/app/builder-services/builder.service';
import { MixpanelService } from 'src/app/tracking/mixpanel.service';
import { VendorService, Vendor } from 'src/app/multivendor/vendor.service';

@Component({
  selector: 'multivendor',
  templateUrl: './multivendor.component.html',
  styleUrls: ['./multivendor.component.scss'] // ✅ use styleUrls (plural)
})
export class MultivendorComponent implements OnInit {
  previewBlock = false;
  previewFields = false;
  styleFields = false; // ✅ style tab is supported now

  blockData: any;
  blockFields: any;
  blockId = '';

  showVendorsProgress = false;

  allVendors: Vendor[] = [];
  shownVendors: Vendor[] = [];

  @Input() nestedBlock: any = {};
  @Input() importedBlockData: any;
  initialData: any = false;

  constructor(
    public blockDataService: BlockDataService,
    private blockFieldsService: BlockFieldsService,
    public builderService: BuilderService,
    private mixPanel: MixpanelService,
    public vendor: VendorService
  ) {}

  ngOnInit() {
    if (this.importedBlockData) {
      this.previewBlock = true;
      this.previewFields = false;
      this.styleFields = false;
    }

    if (this.initialData !== 'initial') {
      if (this.nestedBlock && this.nestedBlock.type === 'multivendor') {
        this.blockData = this.nestedBlock;
        this.blockId = this.nestedBlock.id;
        this.previewBlock = true;
      } else {
          this.blockData = this.importedBlockData ? this.importedBlockData : this.blockFieldsService.multivendor
          // Use the imported block's ID if it exists, otherwise generate a new one
          if (this.importedBlockData && this.importedBlockData.id) {
            this.blockId = this.importedBlockData.id
          } else if (!this.blockId) {
            this.blockId = this.blockDataService.getUniqueId(2)
          }
      }
    } else {
      this.blockData = this.blockDataService.getSelectedBlockData(this.blockId);
    }

    this.getVendors();
  }

  // ============= Data loading =============
  getVendors = () => {
    this.showVendorsProgress = true;

    // Prefer the observable to stay in sync with VendorService refresh()
    this.vendor.vendors.subscribe(
      (list: Vendor[]) => {
        this.showVendorsProgress = false;
        this.allVendors = [];
        if (list && list.length > 0) {
          for (let i = 0; i < list.length; i++) {
            this.allVendors.push(list[i]);
          }
        }
        this.shownVendors = this.allVendors;
        this.setVendorsData(this.blockId, this.allVendors);

        // Ensure blockData exists for adjustArgs
        if (!this.blockData || !this.blockData.data) {
          this.blockData = this.blockFieldsService.multivendor;
        }
        this.adjustArgs(this.blockId, this.blockData.data);
      },
      (err) => {
        this.showVendorsProgress = false;
        this.allVendors = [];
        this.shownVendors = [];
        this.setVendorsData(this.blockId, []);
      }
    );
  };

  // ============= Getters/Setters (builder sync) =============
  getVendorsData = (blockId: string) => {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      return;
    }
    const fields = this.blockDataService.getSelectedBlockData(blockId);
    return fields.vendorsData;
  };

  setVendorsData = (blockId: string, data: Vendor[]) => {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      return;
    }
    const fields = this.blockDataService.getSelectedBlockData(blockId);
    fields.vendorsData = data;
  };

  // ============= Sidebar + Preview controls =============
  addBlockToPreview(initial: any = '') {
    const payload: any = {
      component: MultivendorComponent,
      index: 'latest',
      element: this,
      initial: initial
    };
    this.blockDataService.sendBlockData(payload);
    this.builderService.blocksCollapsed = true;
  }

  appendBlock($event: any) {
    if (!this.builderService.standalone) {
      return false;
    }
    const cloned: any = JSON.parse(JSON.stringify(this.blockData || this.blockFieldsService.multivendor));
    this.blockId = this.blockDataService.appendBlocksData(cloned);
    this.addBlockToPreview();

    this.builderService.logData('Multivendor Block Added', 'Block Add', 'Multivendor Block Added').subscribe(
      () => {},
      () => {}
    );
    this.mixPanel.track('Block Added', {
      projectid: this.builderService.getSelectedProject(),
      block: 'multivendor'
    });
    return true;
  }

  showData(blockId: any) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      blockId = this.blockDataService.getLatestBlockId();
    }
    this.blockFieldsService.clickedBlockId = blockId;
    this.blockDataService.sendCurrentBlock(MultivendorComponent);
  }

  deleteBlock = (blockId: any) => {
    this.blockDataService.removeBlockData(blockId);
  };

  // ============= Tabs visibility helpers (match Categories behavior) =============
  showDataTab = () => {
    this.previewBlock = false;
    this.previewFields = true;
    this.styleFields = false;
  };

  showStyleTab = () => {
    // ✅ enable style tab like Categories
    this.previewBlock = false;
    this.previewFields = false;
    this.styleFields = true;
  };

  checkSidebar = () => {
    if (!this.previewBlock && !this.previewFields && !this.styleFields) {
      return true;
    }
    return false;
  };

  checkPreview = () => {
    if (this.previewBlock && !this.previewFields && !this.styleFields) {
      return true;
    }
    return false;
  };

  checkData = () => {
    if (this.previewFields) {
      return true;
    }
    return false;
  };

  checkStyle = () => {
    // ✅ same logic as Categories
    if (this.styleFields && !this.previewFields && !this.previewBlock) {
      return true;
    }
    return false;
  };

  checkSettings = () => {
    if (!this.checkSidebar() && !this.checkPreview()) {
      return true;
    }
    return false;
  };

  // ============= Query & Adjustment logic (like categories) =============
  queryChange(query: string, blockId: string, $event: any, type: string) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      return;
    }
    const fields = this.blockDataService.getSelectedBlockData(blockId);
    let clonedData: any = {};
    if (fields && fields.data) {
      clonedData = JSON.parse(JSON.stringify(fields.data));
    } else {
      clonedData = this.blockFieldsService.multivendor;
    }

    let value: any = '';
    if (type === 'select') {
      value = $event.value;
    } else if (type === 'text') {
      value = $event.currentTarget.value;
    } else if (type === 'slider') {
      value = $event.currentTarget.value;
    }

    clonedData[query] = value;
    fields.data = clonedData;

    this.adjustArgs(blockId, clonedData);
  }

  adjustArgs = (blockId: string, args: any) => {
    let filtered: Vendor[] = [];

    let limit = args.limit;
    let vendors = args.vendors;
    let type = args.source;
    let order_by = args.orderby;
    let order_type = args.order;

    if (type === 'by_id' && vendors && vendors.length > 0) {
      filtered = this.filterVendorsByIds(vendors);
    } else {
      filtered = this.shownVendors;
    }

    filtered = this.filterLimit(limit, filtered);
    filtered = this.orderVendors(order_by, order_type, filtered);

    this.setVendorsData(blockId, filtered);
  };

  filterLimit = (limit: number, list: Vendor[]) => {
    if (!list) {
      return [];
    }
    if (!limit || limit < 1) {
      return list;
    }
    return list.slice(0, limit);
  };

  filterVendorsByIds = (ids: string[]) => {
    const result: Vendor[] = [];
    if (ids && ids.length > 0) {
      for (let i = 0; i < this.allVendors.length; i++) {
        const v = this.allVendors[i];
        for (let j = 0; j < ids.length; j++) {
          if (v.id === ids[j]) {
            result.push(v);
            break;
          }
        }
      }
    }
    return result;
  };

  // ============= Style handling (mirrors Categories) =============
  styleChange(style: any, blockId: any, $event: any, type: string) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      return;
    }
    const fields = this.blockDataService.getSelectedBlockData(blockId);
    // Clone the style object before mutation
    const clonedStyle = { ...(fields.style || {}) };

    let value: any = '';
    if (type === 'select') {
      value = $event.value;
    } else if (type === 'text') {
      value = $event.currentTarget.value;
    } else if (type === 'slider') {
      value = $event.currentTarget.value + 'px';
    }

    clonedStyle[style] = value;
    fields.style = clonedStyle;
  }

  orderVendors = (order_by: string, order_type: string, list: Vendor[]) => {
    if (!list || list.length === 0) {
      return [];
    }
    if (order_by === 'name') {
      list.sort((a: Vendor, b: Vendor) => {
        const aName = a && a.name ? a.name.toLowerCase() : '';
        const bName = b && b.name ? b.name.toLowerCase() : '';
        if (order_type === 'ASC') {
          return aName.localeCompare(bName);
        } else {
          return bName.localeCompare(aName);
        }
      });
    }
    return list;
  };

  // ============= Misc builder helpers =============
  getBlockFields = (id: string) => {
    if (this.importedBlockData) {
      return this.importedBlockData;
    }
    return this.blockDataService.getSelectedBlockData(id);
  };

  getVendorsListForSelect = () => {
    return this.allVendors || [];
  };
}
